import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import type { ReactNode } from 'react';
import type { WalletTransaction } from '@/api/wallet';
import { formatBRL } from '@/lib/format';

const LABELS: Record<string, string> = {
  credit_pix: 'Saldo adicionado · PIX',
  credit_card: 'Crédito cartão',
  cashback: 'Cashback',
  promo_bonus: 'Bônus promocional',
  refund_reservation: 'Estorno de reserva',
  debit_charging: 'Recarga EV',
  debit_store: 'Loja',
  debit_lounge: 'Lounge',
  debit_amenity: 'Aspirador',
  adjustment: 'Ajuste',
};

function BoltGlyph({ size = 17, fill = '#001B74' }: { size?: number; fill?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} aria-hidden="true">
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

/** 4 quadrados rotacionados 45° — marca do PIX no mockup. */
function PixMark({ size = 15, fill = '#00A664' }: { size?: number; fill?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill={fill} aria-hidden="true">
      <g transform="rotate(45 8 8)">
        <rect x="2.5" y="2.5" width="4.8" height="4.8" rx="1" />
        <rect x="8.7" y="2.5" width="4.8" height="4.8" rx="1" />
        <rect x="2.5" y="8.7" width="4.8" height="4.8" rx="1" />
        <rect x="8.7" y="8.7" width="4.8" height="4.8" rx="1" />
      </g>
    </svg>
  );
}

/** Glifo semântico por tipo de transação (identidade das carteiras). */
function glyphFor(type: string): { bg: string; node: ReactNode } {
  switch (type) {
    case 'debit_charging':
      return { bg: '#E8ECFB', node: <BoltGlyph /> };
    case 'cashback':
    case 'promo_bonus':
      return {
        bg: '#F6ECFA',
        node: (
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 17,
              lineHeight: 1,
              color: '#A23C92'
            }}
          >
            $
          </span>
        ),
      };
    case 'credit_pix':
      return { bg: '#E4F6EC', node: <PixMark /> };
    case 'debit_amenity':
      return {
        bg: 'rgba(0,27,116,0.05)',
        node: (
          <span className="text-[15px]" aria-hidden="true">
            🧹
          </span>
        ),
      };
    case 'adjustment':
      return {
        bg: 'rgba(0,27,116,0.05)',
        node: (
          <span className="text-[15px]" aria-hidden="true">
            ⚙
          </span>
        ),
      };
    default:
      return { bg: 'rgba(0,27,116,0.05)', node: <BoltGlyph /> };
  }
}

function formatSubtitle(dateStr: string, energyKwh?: string): string {
  const time = format(new Date(dateStr), 'HH:mm');
  const kwh = energyKwh
    ? ` · ${Number(energyKwh).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWh`
    : '';
  return `${time}${kwh}`;
}

interface Props {
  tx: WalletTransaction;
  pointName?: string;
  energyKwh?: string;
}

export function TransactionRow({ tx, pointName, energyKwh }: Props) {
  const amount = Number(tx.amount_brl);
  const isCredit = amount >= 0;
  const sign = isCredit ? '+' : '−';
  const label = LABELS[tx.type] ?? tx.type;
  const title =
    tx.type === 'debit_charging' && pointName ? `Recarga · ${pointName}` : label;
  const subtitle = formatSubtitle(
    tx.created_at,
    tx.type === 'debit_charging' ? energyKwh : undefined
  );
  const { bg, node } = glyphFor(tx.type);

  const body = (
    <div className="flex items-center gap-3 py-3">
      <div
        className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full"
        style={{ background: bg }}
      >
        {node}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold leading-tight text-bow-royal">
          {title}
        </span>
        <span className="mt-0.5 truncate text-xs text-bow-royal/50">{subtitle}</span>
      </div>
      {/* Sem chip de carteira: o extrato SEMPRE vem filtrado por uma aba
          (Saldo | Cashback), então repetir o rótulo em toda linha só diz o
          que o cabeçalho já disse. Se um dia existir uma lista misturando as
          duas, o chip volta — `tx.wallet` continua no contrato. */}
      <span
        className="shrink-0 text-sm font-semibold"
        style={{ color: isCredit ? '#00A664' : '#001B74' }}
      >
        {sign} {formatBRL(Math.abs(amount))}
      </span>
    </div>
  );

  if (tx.session_id) {
    return (
      <Link to={`/receipt/${tx.session_id}`} className="block">
        {body}
      </Link>
    );
  }
  return body;
}
